import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin } from 'lucide-react';
import EventStatusBadge from './EventStatusBadge';
import VenueTypeIndicator from './EventVenueIndicator';
import EventTypeBadge from './EventTypeBadge';
import EventRevenueBadge from './EventRevenueBadge';
import { formatTimeWithZone } from '../../utils/utils';


const FloatingRevenueBadge = styled.div`
  position: absolute;
  top: 3.75rem;
  right: 1.5rem;
  z-index: 2;
  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    z-index: 2;
    max-width: 130px
  }
`;

const CardContainer = styled(Link)`
  display: block;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1rem;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s;
  will-change: transform;
  max-width: 100%;
  box-sizing: border-box;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column; /* Stack on mobile */
    gap: 0.5rem;
  }
`;

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0; /* Allows text truncation to work */
  max-width: ${props => props.$hasRevenue ? '60%' : '100%'}; /* Add margin when revenue badge is present */
`;

const EventTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.2rem;
  line-height: 1.2;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    white-space: normal; /* Allow wrapping on mobile */
    font-size: 1rem;
  }
`;

const EventCompany = styled.div`
  color: #888;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 1.2em; /* Maintains consistent height */

  @media (max-width: 768px) {
    white-space: normal; /* Allow wrapping on mobile */
  }
`;

const StatusColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EventDetails = styled.div`
  margin-bottom: 1rem;
`;

const EventInfo = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    color: #666;
  }
`;

const EventDate = styled.div`
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const EventLocation = styled.div`
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    word-break: break-word; /* Allow long addresses to break */
  }
`;

const EventEmployees = styled.div`
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const EventEmployee = styled.div`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 0.5rem;
  background-color: #ededed;
  color:#727272;
  margin-right: 0.5rem;
`;

const AccountRep = styled.div`
  color: #999;
  font-size: 0.75rem;
  font-weight: 400;
  margin-top: 0.15rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

`;

const EventCard = ({ event, eventEmployees, isManager = false }) => {
  const hasRevenue = event.expected_revenue && isManager;
  
  return (
    <CardContainer to={`/events/${event.id}`}>
      {/* Floating Revenue Badge */}
      {hasRevenue && (
        <FloatingRevenueBadge>
          <EventRevenueBadge revenue={event.expected_revenue} size="small" showIcon={false} />
        </FloatingRevenueBadge>
      )}
      
      <TitleContainer>
        <TitleColumn $hasRevenue={hasRevenue}>
          <EventTitle>{event.title}</EventTitle>
          {event.company_name && (
          <EventCompany>
            {event.company_name || ''}
          </EventCompany>
          )}
          {event.account_rep?.name && (
            <AccountRep>
              Sales Rep:
              <span style={{fontWeight: '700'}}>{event.account_rep.name}</span>
            </AccountRep>
          )}
        </TitleColumn>
        <StatusColumn>
          <EventTypeBadge eventType={event.event_type} style={{fontSize: '0.8rem'}} />
          <VenueTypeIndicator venueType={event.venue_type} size="small" />
          <EventStatusBadge style={{fontSize: '0.8rem'}} status={event.event_status} />
        </StatusColumn>
      </TitleContainer>
      <EventDetails>
        <EventInfo>
          <EventDate>
            <CalendarDays size={16} style={{color: 'var(--primary-green)'}} /> 
            {formatTimeWithZone(event.start_time, event.end_time, event.timezone)}
          </EventDate>
        </EventInfo>
        <EventInfo>
          <EventLocation>
            <MapPin size={16} style={{color: 'var(--primary-green)'}} /> {event.location}
          </EventLocation>
        </EventInfo>
        <EventEmployees>
          {eventEmployees.map((employee) => (
            <EventEmployee key={employee.id}>
              {employee.user.name}
            </EventEmployee>
          ))}
        </EventEmployees>
      </EventDetails>
    </CardContainer>
  );
};

export default EventCard;